import React from 'react'
import { motion } from 'framer-motion'
import {
  ShieldCheckIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import TwoFactorSetup from '@/components/ui/TwoFactorSetup'

const TwoFactorSettingsPage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
              <p className="text-gray-600">Enhance your account security with additional verification methods</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600">
            <ShieldCheckIcon className="h-5 w-5" />
            <span>Account Security</span>
          </div>
        </div>
      </motion.div>

      {/* Two-Factor Authentication Setup */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="dashboard-card"
      >
        <TwoFactorSetup />
      </motion.div>

      {/* Security Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="dashboard-card"
      >
        <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
          <ShieldCheckIcon className="h-5 w-5 text-gray-600 mr-2" />
          Security Information
        </h3>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h4 className="font-medium text-green-900 mb-2">🔒 Authenticator Apps</h4>
            <p className="text-sm text-green-700">
              Most secure option. Works offline and provides time-based codes that change every 30 seconds.
            </p>
            <p className="text-xs text-green-600 mt-2 font-medium">Recommended</p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="font-medium text-blue-900 mb-2">📧 Email Verification</h4>
            <p className="text-sm text-blue-700">
              Receive verification codes via email. Good backup option when combined with authenticator.
            </p>
            <p className="text-xs text-blue-600 mt-2 font-medium">Convenient</p>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
            <h4 className="font-medium text-purple-900 mb-2">📱 SMS Verification</h4>
            <p className="text-sm text-purple-700">
              Receive verification codes via SMS to your phone. Available globally with country code support.
            </p>
            <p className="text-xs text-purple-600 mt-2 font-medium">Mobile-friendly</p>
          </div>
        </div>
        
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <ShieldCheckIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h5 className="text-sm font-medium text-yellow-900">Security Best Practices</h5>
              <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                <li>• Enable multiple 2FA methods for redundancy</li>
                <li>• Store backup codes in a secure password manager</li>
                <li>• Never share your 2FA codes with anyone</li>
                <li>• Regularly review and revoke unused sessions</li>
                <li>• Use authenticator apps over SMS when possible</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="dashboard-card"
      >
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <Button
            onClick={() => navigate('/admin/profile')}
            variant="secondary"
            className="h-auto py-4 flex flex-col items-center space-y-2"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to Profile</span>
          </Button>
          
          <Button
            onClick={() => {
              window.open('https://support.google.com/accounts/answer/1066447', '_blank')
            }}
            variant="secondary"
            className="h-auto py-4 flex flex-col items-center space-y-2"
          >
            <ShieldCheckIcon className="h-5 w-5" />
            <span>Learn About 2FA</span>
          </Button>
        </div>
      </motion.div>
    </div>
  )
}

export default TwoFactorSettingsPage